import { useEffect, useState } from "react";
import { Plus, Landmark, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";
import ConfirmDialog from "@/components/shared/ConfirmDialog";

const EMPTY = { bank_name: "", account_number: "", account_holder: "", branch: "", notes: "", is_active: true };

export default function AdminRekening() {
  const [items, setItems] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => axios.get(`${API}/admin/rekening`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (item) => { setEditingId(item.id); setForm({ bank_name: item.bank_name, account_number: item.account_number, account_holder: item.account_holder, branch: item.branch || "", notes: item.notes || "", is_active: item.is_active }); setModalOpen(true); };

  const handleSubmit = async () => {
    if (!form.bank_name || !form.account_number || !form.account_holder) return toast.error("Mohon lengkapi data rekening");
    try {
      if (editingId) {
        await axios.put(`${API}/admin/rekening/${editingId}`, form);
        toast.success("Rekening berhasil diperbarui");
      } else {
        await axios.post(`${API}/admin/rekening`, form);
        toast.success("Rekening berhasil ditambahkan");
      }
      setModalOpen(false);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/rekening/${deleteId}`);
      toast.success("Rekening berhasil dihapus");
      setDeleteId(null);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div data-testid="admin-rekening-page" className="space-y-5">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Rekening Pembayaran</h1>
          <p className="text-sm text-[#64748B]">Kelola rekening bank untuk transfer manual pembayaran jamaah.</p>
        </div>
        <Button data-testid="admin-rekening-add-button" onClick={openCreate} className="gap-2 rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]"><Plus className="h-4 w-4" /> Tambah Rekening</Button>
      </div>

      {items === null ? <LoadingScreen /> : items.length === 0 ? (
        <EmptyState icon={Landmark} title="Belum ada rekening" description="Tambahkan rekening bank pertama untuk menerima transfer jamaah." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white">
          <Table data-testid="admin-rekening-table">
            <TableHeader><TableRow><TableHead>Bank</TableHead><TableHead>No. Rekening</TableHead><TableHead>Atas Nama</TableHead><TableHead>Cabang</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((r) => (
                <TableRow key={r.id} data-testid={`admin-rekening-row-${r.id}`}>
                  <TableCell className="font-medium text-[#1F2937]">{r.bank_name}</TableCell>
                  <TableCell className="font-mono text-[#64748B]">{r.account_number}</TableCell>
                  <TableCell className="text-[#64748B]">{r.account_holder}</TableCell>
                  <TableCell className="text-[#64748B]">{r.branch || "-"}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${r.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                      {r.is_active ? "Aktif" : "Nonaktif"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="icon" variant="outline" data-testid={`admin-rekening-edit-${r.id}`} onClick={() => openEdit(r)} className="h-8 w-8 rounded-full"><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="outline" data-testid={`admin-rekening-delete-${r.id}`} onClick={() => setDeleteId(r.id)} className="h-8 w-8 rounded-full border-red-200 text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="admin-rekening-modal">
          <DialogHeader><DialogTitle>{editingId ? "Edit Rekening" : "Tambah Rekening"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama Bank</Label><Input data-testid="rekening-form-bank-name" value={form.bank_name} onChange={(e) => update("bank_name", e.target.value)} placeholder="Bank Syariah Indonesia (BSI)" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nomor Rekening</Label><Input data-testid="rekening-form-account-number" value={form.account_number} onChange={(e) => update("account_number", e.target.value)} /></div>
              <div><Label>Cabang (Opsional)</Label><Input data-testid="rekening-form-branch" value={form.branch} onChange={(e) => update("branch", e.target.value)} /></div>
            </div>
            <div><Label>Atas Nama</Label><Input data-testid="rekening-form-account-holder" value={form.account_holder} onChange={(e) => update("account_holder", e.target.value)} /></div>
            <div><Label>Catatan (Opsional)</Label><Textarea data-testid="rekening-form-notes" value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} /></div>
            <div className="flex items-center justify-between rounded-xl border border-slate-100 p-3">
              <Label>Tampilkan ke Jamaah</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => update("is_active", v)} data-testid="rekening-form-active-switch" />
            </div>
            <Button data-testid="admin-rekening-submit-button" onClick={handleSubmit} className="w-full rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">Simpan</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(v) => !v && setDeleteId(null)}
        title="Hapus Rekening?"
        description="Rekening ini tidak akan tampil lagi di halaman pembayaran jamaah."
        onConfirm={handleDelete}
        testId="admin-rekening-delete-confirm"
      />
    </div>
  );
}
